import { AppThunk } from "@app/redux/store/root";
import {
    APP_LIST,
    SELECTED_APP_OEM,
    COUNTRIES_FETCHED_OEM,
    SELECTED_COUNTRIES_UPDATED_OEM,
    START_DATE_FETCHED_OEM,
    SELECTED_DATE_RANGE_UPDATED_OEM,
    OEM_COUNTRY_DISTRIBUTION,
    RESET_OEM_ANALYTICS_FILTERS,
    CHART_DATA_FETCHED_OEM,
    RESET_ALL,
    VERSIONS_FETCHED_OEM,
    MODELS_FETCHED_OEM,
    SELECTED_MODELS_UPDATED,
    SELECTED_VERSIONS_UPDATED_OEM,
    OEM_APP_STORE_ENGAGEMENT_DATA,
    TOTAL_USERS_FETCHED,
    APPS_PER_USER_FETCHED,
    USER_GROWTH_FETCHED,
} from "./types";
import { fetchAppInstallsForDeveloperApi } from "@app/redux/services/devAnalytics";
import TokenService from "@app/redux/services/token";
import {
    fetchVersionForDeveloperAppApi,
    fetchCountriesForOemAppApi,
    fetchModelsForOemAppApi,
    fetchStartDateForSelectedAppApi,
    fetchChartDataApi,
    fetchCountryDistributionApi,
    fetchOemAppStoreEngagementApi,
    fetchTotalUsersApi,
    fetchUserGrowthApi,
    fetchAppsPerUserApi,
} from "@app/redux/services/oemAnalytics";

export const fetchAppsList =
    (token: string): AppThunk =>
    async (dispatch): Promise<any> => {
        try {
            const response = await fetchAppInstallsForDeveloperApi(token);
            dispatch({
                type: APP_LIST,
                payload: response.data,
            });
        } catch (err: any) {
            return Promise.reject(err);
        }
    };

export const fetchVersions =
    (token: string, appId: string): AppThunk =>
    async (dispatch): Promise<any> => {
        try {
            const response = await fetchVersionForDeveloperAppApi(token, appId);
            dispatch({
                type: VERSIONS_FETCHED_OEM,
                payload: response,
            });
        } catch (err: any) {
            return Promise.reject(err);
        }
    };

interface SelectedVersion {
    id: string;
    name: string;
}

export const updateSelectedVersions =
    (newSelectedVersions: Array<SelectedVersion>): AppThunk =>
    async (dispatch): Promise<any> => {
        dispatch({
            type: SELECTED_VERSIONS_UPDATED_OEM,
            payload: newSelectedVersions,
        });
    };

interface SelectedApp {
    id: string;
    name: string;
    installsCount: number;
    oemAppId: string;
}
export const updateSelectedApp =
    (newSelectedApp: SelectedApp): AppThunk =>
    async (dispatch): Promise<any> => {
        dispatch({
            type: SELECTED_APP_OEM,
            payload: newSelectedApp,
        });
        dispatch({ type: RESET_OEM_ANALYTICS_FILTERS });
    };

export const resetAll =
    (): AppThunk =>
    async (dispatch): Promise<any> => {
        dispatch({
            type: RESET_ALL,
        });
    };

export const fetchCountries =
    (token: string, appId: string): AppThunk =>
    async (dispatch): Promise<any> => {
        try {
            const response = await fetchCountriesForOemAppApi(token, appId);
            dispatch({
                type: COUNTRIES_FETCHED_OEM,
                payload: response,
            });
        } catch (err: any) {
            return Promise.reject(err);
        }
    };

export const fetchModels =
    (token: string, appId: string): AppThunk =>
    async (dispatch): Promise<any> => {
        try {
            const response = await fetchModelsForOemAppApi(token, appId);
            dispatch({
                type: MODELS_FETCHED_OEM,
                payload: response,
            });
        } catch (err: any) {
            return Promise.reject(err);
        }
    };

interface SelectedCountry {
    id: string;
    name: string;
}

export const updateSelectedCountries =
    (newSelectedCountries: Array<SelectedCountry>): AppThunk =>
    async (dispatch): Promise<any> => {
        dispatch({
            type: SELECTED_COUNTRIES_UPDATED_OEM,
            payload: newSelectedCountries,
        });
    };

export const updateSelectedModels =
    (newSelectedModels: Array<SelectedCountry>): AppThunk =>
    async (dispatch): Promise<any> => {
        dispatch({
            type: SELECTED_MODELS_UPDATED,
            payload: newSelectedModels,
        });
    };

export const fetchStartDateForSelectedApp =
    (token: string, appId: string): AppThunk =>
    async (dispatch): Promise<any> => {
        try {
            const response = await fetchStartDateForSelectedAppApi(token, appId);
            dispatch({
                type: START_DATE_FETCHED_OEM,
                payload: response,
            });
        } catch (err: any) {
            return Promise.reject(err);
        }
    };

export const fetchOemCountryDistribution =
    (applicationId: string): AppThunk =>
    async (dispatch): Promise<any> => {
        try {
            const response = await fetchCountryDistributionApi(
                TokenService.getLocalAccessToken(),
                applicationId
            );

            dispatch({
                type: OEM_COUNTRY_DISTRIBUTION,
                payload: response,
            });
        } catch (err: any) {
            return Promise.reject(err);
        }
    };

export const updateSelectedDateRange =
    (newSelectedDateRange: Array<Date>, selectedPreDefinedDateRange: string): AppThunk =>
    async (dispatch): Promise<any> => {
        dispatch({
            type: SELECTED_DATE_RANGE_UPDATED_OEM,
            payload: { newSelectedDateRange, selectedPreDefinedDateRange },
        });
    };

export const resetOemAnalyticsFilters =
    (): AppThunk =>
    async (dispatch): Promise<any> => {
        dispatch({
            type: RESET_OEM_ANALYTICS_FILTERS,
        });
    };

export const fetchChartData =
    (
        appId: string,
        versions: Array<SelectedVersion>,
        countries: Array<SelectedCountry>,
        models: Array<SelectedCountry>,
        dateRange: Array<Date>,
        token: string,
        view = ""
    ): AppThunk =>
    async (dispatch): Promise<any> => {
        try {
            if (
                (countries && countries.length === 0) ||
                (versions && versions.length === 0) ||
                (models && models.length === 0)
            ) {
                dispatch({
                    type: CHART_DATA_FETCHED_OEM,
                    payload: { data: { data: [] } },
                });
            } else {
                const response = await fetchChartDataApi(
                    appId,
                    versions,
                    countries,
                    models,
                    dateRange,
                    token,
                    view
                );
                dispatch({
                    type: CHART_DATA_FETCHED_OEM,
                    payload: response,
                });
            }
        } catch (err: any) {
            return Promise.reject(err);
        }
    };

export const fetchOemAppStoreEngagement =
    (): AppThunk =>
    async (dispatch): Promise<any> => {
        try {
            const response = await fetchOemAppStoreEngagementApi(
                TokenService.getLocalAccessToken()
            );

            dispatch({
                type: OEM_APP_STORE_ENGAGEMENT_DATA,
                payload: response,
            });
        } catch (err: any) {
            return Promise.reject(err);
        }
    };

export const fetchTotalUsers =
    (token: string): AppThunk =>
    async (dispatch): Promise<any> => {
        try {
            // dispatch({
            //     type: GENERATE_KEY_PAIR,
            //     payload: { status: false }
            // })

            const response = await fetchTotalUsersApi(token);
            dispatch({
                type: TOTAL_USERS_FETCHED,
                payload: response,
            });
            // console.log(response);
            // return Promise.resolve(response);
        } catch (err: any) {
            return Promise.reject(err);
        } finally {
            // dispatch({
            //     type: GENERATE_KEY_PAIR,
            //     payload: { status: false },
            // });
        }
    };

export const fetchUserGrowth =
    (token: string, since: string): AppThunk =>
    async (dispatch): Promise<any> => {
        try {
            // dispatch({
            //     type: GENERATE_KEY_PAIR,
            //     payload: { status: false }
            // })

            const response = await fetchUserGrowthApi(token, since);
            dispatch({
                type: USER_GROWTH_FETCHED,
                payload: response,
            });
            // console.log(response);
            // return Promise.resolve(response);
        } catch (err: any) {
            return Promise.reject(err);
        } finally {
            // dispatch({
            //     type: GENERATE_KEY_PAIR,
            //     payload: { status: false },
            // });
        }
    };

export const fetchAppsPerUser =
    (token: string): AppThunk =>
    async (dispatch): Promise<any> => {
        try {
            const response = await fetchAppsPerUserApi(token);

            dispatch({
                type: APPS_PER_USER_FETCHED,
                payload: response,
            });
        } catch (err: any) {
            return Promise.reject(err);
        }
    };
